"use client";

import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  Search,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
} from "lucide-react";
import { useFavorites } from "@/hooks/useFavorites";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import navIndex from "@/data/nav/categoryIndex.json";
import { CategorySearchDialog } from "@/components/CategorySearch";
import { getCategoryUrl, getSubcategoryUrl } from "@/lib/categoryUrls";
import { getSubcategoryIcon } from "@/lib/subcategoryIcons";

const NAV_LABELS: Record<string, string> = {
  "fantasy-names": "Fantasy",
  "real-names": "Real Names",
  "places-locations": "Places",
  "pop-culture": "Pop Culture",
  "other-names": "Other Names",
  "description-generators": "Worldbuilding",
};

export default function Navbar() {
  const { favorites } = useFavorites();
  const pathname = usePathname();
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenCat(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenCat(null);
  }, [pathname]);

  const navCategories = React.useMemo(() => {
    const desiredSlugs = [
      "description-generators",
      "fantasy-names",
      "pop-culture",
      "places-locations",
      "real-names",
    ];
    return desiredSlugs
      .map((slug) => navIndex.categories.find((c) => c.slug === slug))
      .filter(Boolean) as typeof navIndex.categories;
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/40 backdrop-blur-xl bg-background/80"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-6 lg:gap-8">
            {/* Logo */}
            <Link href="/" className="flex items-center group shrink-0 transition-transform duration-200 hover:scale-[1.02]">
              <Image
                src="/images/Logo/logo-light.png"
                alt="Name Arcane"
                width={170}
                height={24}
                className="h-5 sm:h-6 md:h-7 w-auto object-contain dark:hidden"
                priority
              />
              <Image
                src="/images/Logo/logo-dark.png"
                alt="Name Arcane"
                width={170}
                height={24}
                className="h-5 sm:h-6 md:h-7 w-auto object-contain hidden dark:block"
                priority
              />
            </Link>

            {/* Desktop Navigation */}
            <nav
              ref={dropdownRef}
              className="hidden xl:flex items-center gap-1 shrink-0"
            >
              {/* Mega-menu categories */}
              {navCategories.map((category, index) => {
                const isOpen = openCat === category.slug;
                const shortLabel = NAV_LABELS[category.slug] || category.name;
                const subCount = category.subcategories?.length || 0;

                // Smart positioning to prevent overflowing viewport edges
                const alignmentClass =
                  index === 0
                    ? "left-0"
                    : index === 1
                    ? "left-0 xl:left-1/2 xl:-translate-x-1/2"
                    : index === navCategories.length - 1
                    ? "right-0"
                    : index === navCategories.length - 2
                    ? "right-0 xl:left-1/2 xl:-translate-x-1/2"
                    : "left-1/2 -translate-x-1/2";

                return (
                  <div key={category.slug} className="relative">
                    <button
                      onClick={() => setOpenCat(isOpen ? null : category.slug)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                        isOpen
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <span className="text-sm">{category.icon}</span>
                      <span>{shortLabel}</span>
                      <ChevronDown
                        className={`w-3 h-3 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-primary" : "text-muted-foreground/70"
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.96 }}
                          transition={{ duration: 0.18 }}
                          className={`absolute top-full mt-2 w-[540px] max-w-[calc(100vw-2rem)] rounded-3xl border border-border bg-popover text-popover-foreground p-5 shadow-2xl backdrop-blur-2xl z-50 ${alignmentClass}`}
                        >
                          <div className="flex items-center justify-between pb-3 mb-3 border-b border-border/60 gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-lg shrink-0">{category.icon}</span>
                              <span className="font-semibold text-sm text-foreground truncate">
                                {category.name}
                              </span>
                              <Badge variant="secondary" className="text-[10px] py-0 px-2 shrink-0">
                                Themes
                              </Badge>
                            </div>
                            <Link
                              href={getCategoryUrl(category.slug)}
                              className="text-xs font-semibold text-primary hover:underline flex items-center gap-1 shrink-0 whitespace-nowrap"
                              onClick={() => setOpenCat(null)}
                            >
                              <span>Explore Entire Realm</span>
                              <ArrowRight className="w-3 h-3" />
                            </Link>
                          </div>

                          <div className="grid grid-cols-2 gap-1.5 max-h-[340px] overflow-y-auto pr-1">
                            {category.subcategories.slice(0, 40).map((sub) => (
                              <Link
                                key={sub.slug}
                                href={getSubcategoryUrl(category.slug, sub.slug)}
                                onClick={() => setOpenCat(null)}
                                className="group flex items-center justify-between p-2 rounded-xl text-xs font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                              >
                                <div className="flex items-center gap-2 truncate pr-1">
                                  <span className="text-sm shrink-0">
                                    {getSubcategoryIcon(sub.name || sub.slug, category.slug)}
                                  </span>
                                  <span className="truncate">{sub.name}</span>
                                </div>
                              </Link>
                            ))}
                          </div>

                          {subCount > 40 && (
                            <div className="pt-3 mt-2 border-t border-border/50 text-center">
                              <Link
                                href={getCategoryUrl(category.slug)}
                                onClick={() => setOpenCat(null)}
                                className="text-xs font-semibold text-primary hover:underline"
                              >
                                + View full index &rarr;
                              </Link>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </nav>

            {/* Right Action Icons */}
            <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0">
              {/* Quick Search Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchDialogOpen(true)}
                className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-foreground h-9 px-2.5 sm:px-3 rounded-xl border-border/60 hover:border-primary/40 bg-background/50 shrink-0"
              >
                <Search className="w-4 h-4 text-primary shrink-0" />
                <span className="text-xs hidden 2xl:inline whitespace-nowrap">Search realms…</span>
                <kbd className="pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  ⌘K
                </kbd>
              </Button>

              {/* Favorites Link */}
              <Link href="/favorites">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative rounded-xl text-foreground hover:bg-muted/80 h-9 w-9 shrink-0"
                  aria-label="Favorites"
                >
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-rose-500" />
                  {favorites.length > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-[9px] font-bold text-white flex items-center justify-center shadow-md shadow-rose-500/30"
                    >
                      {favorites.length}
                    </motion.span>
                  )}
                </Button>
              </Link>

              {/* Theme Switcher */}
              <ThemeToggle />

              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="xl:hidden rounded-xl h-9 w-9 shrink-0"
                aria-label="Toggle Navigation"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="xl:hidden border-b border-border/80 bg-background/95 backdrop-blur-xl px-4 pt-3 pb-6 max-h-[85vh] overflow-y-auto"
            >
              <div className="space-y-4">
                {/* Mobile Search Button */}
                <Button
                  variant="outline"
                  className="w-full justify-between rounded-xl h-10 text-muted-foreground"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setSearchDialogOpen(true);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-primary" />
                    <span className="text-xs">Search naming realms & lore…</span>
                  </div>
                  <kbd className="h-5 px-1.5 rounded bg-muted text-[10px] font-mono">⌘K</kbd>
                </Button>

                {/* Categories Accordion */}
                <div className="space-y-2">
                  {navIndex.categories.map((category) => {
                    const isExpanded = openCat === category.slug;
                    return (
                      <div
                        key={category.slug}
                        className="rounded-2xl border border-border/60 overflow-hidden bg-card/40"
                      >
                        <button
                          onClick={() => setOpenCat(isExpanded ? null : category.slug)}
                          className="w-full flex items-center justify-between p-3.5 text-sm font-semibold text-foreground text-left"
                        >
                          <div className="flex items-center gap-2.5">
                            <span className="text-lg">{category.icon}</span>
                            <span>{category.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                isExpanded ? "rotate-180 text-primary" : "text-muted-foreground"
                              }`}
                            />
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="p-3 bg-muted/20 border-t border-border/40 grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-[260px] overflow-y-auto">
                            {category.subcategories.map((sub) => (
                              <Link
                                key={sub.slug}
                                href={getSubcategoryUrl(category.slug, sub.slug)}
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center justify-between p-2 rounded-lg text-xs font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                              >
                                <div className="flex items-center gap-2 truncate pr-1">
                                  <span className="text-sm shrink-0">
                                    {getSubcategoryIcon(sub.name || sub.slug, category.slug)}
                                  </span>
                                  <span className="truncate">{sub.name}</span>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Quick Link to Favorites */}
                <Link
                  href="/favorites"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between p-3.5 rounded-2xl border border-rose-500/20 bg-rose-500/5 text-sm font-semibold text-rose-500"
                >
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    <span>My Saved Favorites</span>
                  </div>
                  <Badge variant="outline" className="border-rose-500/40 text-rose-500">
                    {favorites.length}
                  </Badge>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Global Command Palette (⌘K) */}
      <CategorySearchDialog
        open={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
      />
    </>
  );
}
